<template>
  <div class="sp-wrap">
    <!-- Заголовок страницы -->
    <div class="sp-header">
      <div>
        <h1 class="sp-title">Наши услуги</h1>
        <p class="sp-sub">Профессиональный ремонт всех моделей смартфонов и планшетов</p>
      </div>
      <router-link to="/orders/create" class="sp-btn-order">+ Оформить заказ</router-link>
    </div>

    <!-- Статистика услуг -->
    <div class="sp-stats">
      <div class="sp-stat"><span class="sp-stat-val">50+</span><span class="sp-stat-lbl">Видов ремонта</span></div>
      <div class="sp-stat"><span class="sp-stat-val">20–60 мин</span><span class="sp-stat-lbl">Среднее время</span></div>
      <div class="sp-stat"><span class="sp-stat-val">12 мес</span><span class="sp-stat-lbl">Гарантия</span></div>
      <div class="sp-stat"><span class="sp-stat-val">Бесплатно</span><span class="sp-stat-lbl">Диагностика</span></div>
    </div>

    <!-- Фильтры -->
    <div class="sp-filters">
      <div class="sp-filter-group">
        <label class="sp-filter-label">Категория</label>
        <select v-model="filters.category" @change="applyFilters" class="sp-select">
          <option value="">Всі категорії</option>
          <option value="screen">📱 Заміна екрана</option>
          <option value="battery">🔋 Заміна батареї</option>
          <option value="motherboard">🔧 Ремонт плати</option>
          <option value="water-damage">💧 Після води</option>
          <option value="software">💻 Програмний збій</option>
          <option value="camera">📷 Заміна камери</option>
          <option value="charging">⚡ Роз'єм зарядки</option>
          <option value="other">🔩 Інше</option>
        </select>
      </div>
      <div class="sp-filter-group">
        <label class="sp-filter-label">Цена от (₴)</label>
        <input v-model.number="filters.minPrice" @change="applyFilters" type="number" min="0" class="sp-input" placeholder="0" />
      </div>
      <div class="sp-filter-group">
        <label class="sp-filter-label">Цена до (₴)</label>
        <input v-model.number="filters.maxPrice" @change="applyFilters" type="number" min="0" class="sp-input" placeholder="10000" />
      </div>
      <div class="sp-filter-group">
        <label class="sp-filter-label">Поиск</label>
        <input v-model="filters.search" @input="applyFilters" type="text" class="sp-input" placeholder="Название услуги..." />
      </div>
    </div>

    <!-- Активные фильтры -->
    <div v-if="hasActiveFilters" class="sp-active-filters">
      <span class="sp-filter-tag" v-if="filters.category">
        {{ categoryLabel(filters.category) }}
        <button @click="filters.category = ''; applyFilters()">×</button>
      </span>
      <span class="sp-filter-tag" v-if="filters.minPrice">
        от ₴{{ filters.minPrice }}
        <button @click="filters.minPrice = null; applyFilters()">×</button>
      </span>
      <span class="sp-filter-tag" v-if="filters.maxPrice">
        до ₴{{ filters.maxPrice }}
        <button @click="filters.maxPrice = null; applyFilters()">×</button>
      </span>
      <span class="sp-filter-tag" v-if="filters.search">
        "{{ filters.search }}"
        <button @click="filters.search = ''; applyFilters()">×</button>
      </span>
      <button class="sp-clear-all" @click="clearFilters">Очистить всё</button>
    </div>

    <!-- Загрузка -->
    <div v-if="loading" class="sp-loading">
      <div class="sp-spinner"></div>
      Загружаем услуги...
    </div>

    <!-- Пусто -->
    <div v-else-if="displayServices.length === 0" class="sp-empty">
      <div class="sp-empty-icon">🔍</div>
      <h2>Услуги не найдены</h2>
      <p>Попробуйте изменить параметры фильтрации или <button class="sp-link-btn" @click="clearFilters">сбросьте фильтры</button></p>
    </div>

    <!-- Сетка услуг -->
    <div v-else class="sp-grid">
      <div v-for="service in displayServices" :key="service._id || service.id" class="sp-card">
        <!-- Иконка категории -->
        <div class="sp-card-icon-wrap" :class="getCategoryColor(service.category)">
          <span class="sp-card-icon">{{ getCategoryIcon(service.category) }}</span>
        </div>

        <!-- Бейдж популярности -->
        <div v-if="service.popular" class="sp-popular-badge">🔥 Популярно</div>

        <!-- Инфо -->
        <div class="sp-card-body">
          <div class="sp-card-category">{{ categoryLabel(service.category) }}</div>
          <h3 class="sp-card-name">{{ service.name }}</h3>
          <p class="sp-card-desc">{{ service.description }}</p>
        </div>

        <!-- Совместимость -->
        <div v-if="service.compatibility && service.compatibility.length" class="sp-compat">
          <span v-for="device in service.compatibility.slice(0, 3)" :key="device" class="sp-compat-tag">{{ device }}</span>
          <span v-if="service.compatibility.length > 3" class="sp-compat-more">+{{ service.compatibility.length - 3 }}</span>
        </div>

        <!-- Время и цена -->
        <div class="sp-card-meta">
          <div class="sp-meta-time">
            <span class="sp-meta-icon">⏱</span>
            <span>~{{ service.estimatedTime || '1' }} ч.</span>
          </div>
          <div class="sp-meta-price">₴{{ service.price }}</div>
        </div>

        <!-- Кнопка -->
        <button @click="orderService(service)" class="sp-card-btn">
          Заказать ремонт
        </button>
      </div>
    </div>

    <!-- CTA блок -->
    <div class="sp-cta">
      <div class="sp-cta-content">
        <div class="sp-cta-icon">🤔</div>
        <div>
          <h3>Не нашли свою поломку?</h3>
          <p>Опишите проблему — мы сделаем диагностику бесплатно и предложим решение</p>
        </div>
      </div>
      <router-link to="/orders/create" class="sp-cta-btn">Описать проблему</router-link>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// === ПРАЙС-ЛИСТ MobiFIX PRO === (середні ціни по ринку Харкова, 2026)
const FALLBACK_SERVICES = [
  // --- ЕКРАН ---
  { id: 1,  name: 'Заміна екрана iPhone (12–16 Pro Max)', category: 'screen', description: 'Заміна оригінального OLED-дисплея. Зберігаємо True Tone і Face ID. Гарантія 12 міс.', price: 4200, estimatedTime: 1, popular: true,  compatibility: ['iPhone 12', 'iPhone 13', 'iPhone 14', 'iPhone 15', 'iPhone 16'] },
  { id: 2,  name: 'Заміна екрана iPhone (8–11)', category: 'screen', description: 'Замінюємо дисплейний модуль на оригінальний або сертифікований. Від 45 хвилин.', price: 1600, estimatedTime: 0.8, popular: false, compatibility: ['iPhone 8', 'iPhone X', 'iPhone XS', 'iPhone 11'] },
  { id: 3,  name: 'Заміна екрана Samsung Galaxy S-серія', category: 'screen', description: 'Оригінальна AMOLED-матриця Samsung. Зберігаємо сканер відбитка під екраном.', price: 2800, estimatedTime: 1, popular: true,  compatibility: ['Galaxy S21', 'Galaxy S22', 'Galaxy S23', 'Galaxy S24'] },
  { id: 4,  name: 'Заміна екрана Samsung Galaxy A-серія', category: 'screen', description: 'Доступна заміна дисплея для популярних моделей серії A. Швидко і якісно.', price: 1400, estimatedTime: 0.8, popular: false, compatibility: ['Galaxy A53', 'Galaxy A54', 'Galaxy A34', 'Galaxy A14'] },
  { id: 5,  name: 'Заміна екрана Xiaomi / Redmi / POCO', category: 'screen', description: 'Заміна дисплея на оригінальний або сертифікований аналог. Гарантія 6 міс.', price: 1100, estimatedTime: 0.8, popular: false, compatibility: ['Xiaomi 13', 'Redmi Note 12', 'POCO X5', 'Redmi 12'] },
  { id: 6,  name: 'Заміна тільки скла (без заміни матриці)', category: 'screen', description: 'Якщо матриця ціла, а розбите лише скло — замінимо лише скло. Значно дешевше!', price: 1100, estimatedTime: 1.5, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },

  // --- БАТАРЕЯ ---
  { id: 7,  name: 'Заміна батареї iPhone', category: 'battery', description: 'Оригінальна батарея Apple або сертифікований аналог. Ємність відновлюється до 100%. 30 хв.', price: 1100, estimatedTime: 0.5, popular: true,  compatibility: ['iPhone 8', 'iPhone X', 'iPhone 11', 'iPhone 12', 'iPhone 13', 'iPhone 14', 'iPhone 15'] },
  { id: 8,  name: 'Заміна батареї Samsung', category: 'battery', description: 'Відновлюємо час роботи вашого Samsung. Оригінальні батареї від перевірених постачальників.', price: 650,  estimatedTime: 0.5, popular: false, compatibility: ['Galaxy S21', 'Galaxy S22', 'Galaxy A53', 'Galaxy A14'] },
  { id: 9,  name: 'Заміна батареї Xiaomi / Redmi', category: 'battery', description: 'Швидка заміна акумулятора для Xiaomi. Від 20 хвилин, гарантія 6 місяців.', price: 450,  estimatedTime: 0.4, popular: false, compatibility: ['Xiaomi 12', 'Redmi Note 12', 'POCO X5', 'Redmi 10'] },
  { id: 10, name: 'Заміна батареї Huawei / Honor', category: 'battery', description: 'Відновлення ємності для пристроїв Huawei та Honor. Оригінальні компоненти.', price: 500,  estimatedTime: 0.5, popular: false, compatibility: ['Huawei P30', 'Honor 90', 'Huawei Nova 10'] },

  // --- ВОДА ---
  { id: 11, name: 'Ремонт після потрапляння води (iPhone)', category: 'water-damage', description: 'Ультразвукова чистка, сушіння, заміна пошкоджених компонентів. Рятуємо 85% "утопленників".', price: 1500, estimatedTime: 3, popular: false, compatibility: ['iPhone 8', 'iPhone X', 'iPhone 11', 'iPhone 12', 'iPhone 13', 'iPhone 14'] },
  { id: 12, name: 'Ремонт після потрапляння води (Android)', category: 'water-damage', description: 'Повна діагностика, чистка та відновлення після контакту з рідиною. Для Samsung, Xiaomi та інших.', price: 950,  estimatedTime: 3, popular: false, compatibility: ['Samsung', 'Xiaomi', 'Huawei', 'OnePlus', 'Google Pixel'] },

  // --- РОЗ'ЄМ ЗАРЯДКИ ---
  { id: 13, name: "Заміна роз'єму зарядки Lightning (iPhone)", category: 'charging', description: "Заміна Lightning-роз'єму на оригінальний. Телефон знову заряджається стабільно.", price: 1200, estimatedTime: 0.5, popular: false, compatibility: ['iPhone 8', 'iPhone X', 'iPhone 11', 'iPhone 12', 'iPhone 13'] },
  { id: 14, name: "Заміна роз'єму USB-C (iPhone 15+, Samsung, Xiaomi)", category: 'charging', description: "Заміна USB-C роз'єму. Підходить для сучасних пристроїв з Type-C.", price: 850,  estimatedTime: 0.5, popular: false, compatibility: ['iPhone 15', 'iPhone 16', 'Samsung', 'Xiaomi', 'Huawei'] },

  // --- КАМЕРА ---
  { id: 15, name: 'Заміна основної камери iPhone', category: 'camera', description: 'Замінюємо основний модуль камери. Відновлюємо якість фото та відео, автофокус, стабілізацію.', price: 3200, estimatedTime: 1, popular: false, compatibility: ['iPhone 12', 'iPhone 13', 'iPhone 14', 'iPhone 15'] },
  { id: 16, name: 'Заміна фронтальної камери', category: 'camera', description: 'Заміна селфі-камери для всіх брендів. Відновлюємо якість та функцію Face ID/сканера обличчя.', price: 1400, estimatedTime: 1, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },
  { id: 17, name: 'Заміна камери Samsung Galaxy', category: 'camera', description: 'Оригінальний модуль камери Samsung. Відновлюємо всі режими зйомки та якість.', price: 2200, estimatedTime: 1, popular: false, compatibility: ['Galaxy S21', 'Galaxy S22', 'Galaxy S23', 'Galaxy A54'] },

  // --- ПЛАТА ---
  { id: 18, name: 'Ремонт материнської плати (BGA)', category: 'motherboard', description: 'Пайка компонентів, реболінг, заміна мікросхем. Найскладніші випадки — наш профіль.', price: 3500, estimatedTime: 4, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },
  { id: 19, name: 'Ремонт "не вмикається"', category: 'motherboard', description: 'Діагностика та ремонт пристроїв, що не вмикаються. Визначаємо причину і усуваємо її.', price: 2000, estimatedTime: 3, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Будь-який бренд'] },

  // --- ПРОГРАМНЕ ЗАБЕЗПЕЧЕННЯ ---
  { id: 20, name: 'Прошивка / відновлення ПЗ', category: 'software', description: 'Перепрошивка, скидання до заводських налаштувань, видалення вірусів, відновлення після збою.', price: 500,  estimatedTime: 1, popular: false, compatibility: ['Android', 'iOS'] },
  { id: 21, name: 'Відновлення даних', category: 'software', description: 'Відновлюємо фото, контакти, повідомлення після скидання або апаратної поломки.', price: 900,  estimatedTime: 2, popular: false, compatibility: ['Android', 'iOS'] },
  { id: 22, name: 'Видалення вірусів / рекламного ПЗ', category: 'software', description: 'Очищаємо пристрій від шкідливого ПЗ, відновлюємо нормальну роботу системи.', price: 500,  estimatedTime: 0.5, popular: false, compatibility: ['Android'] },

  // --- ІНШЕ ---
  { id: 23, name: 'Ремонт динаміка / мікрофона', category: 'other', description: 'Тихий звук, хрипить, співрозмовник не чує — замінюємо динамік або мікрофон.', price: 450,  estimatedTime: 0.8, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Будь-який бренд'] },
  { id: 24, name: 'Заміна кнопок Power / Volume / Home', category: 'other', description: 'Не працює кнопка увімкнення, гучності або Home — замінюємо швидко і якісно.', price: 400,  estimatedTime: 0.8, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi'] },
  { id: 25, name: 'Ремонт Face ID / Touch ID', category: 'other', description: 'Не розпізнає обличчя або відбиток. Замінюємо датчик, відновлюємо функцію.', price: 1200, estimatedTime: 1.5, popular: false, compatibility: ['iPhone', 'Samsung'] },
  { id: 26, name: 'Ремонт Wi-Fi / Bluetooth / GPS антени', category: 'other', description: 'Слабкий сигнал, не підключається до Wi-Fi або Bluetooth — ремонт антен та чипів.', price: 800,  estimatedTime: 1.5, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi', 'Huawei'] },
  { id: 27, name: 'Заміна задньої кришки / корпусу', category: 'other', description: 'Розбита задня кришка або пошкоджений корпус — замінимо на оригінальний або якісний аналог.', price: 600,  estimatedTime: 1, popular: false, compatibility: ['iPhone', 'Samsung', 'Xiaomi'] },
];

export default {
  name: 'ServicesPage',
  setup() {
    const router = useRouter();
    const services = ref([]);
    const loading = ref(false);
    const filters = ref({ category: '', minPrice: null, maxPrice: null, search: '' });

    const categoryMap = {
      screen: '📱 Заміна екрана',
      battery: '🔋 Заміна батареї',
      motherboard: '🔧 Ремонт плати',
      'water-damage': '💧 Після води',
      software: '💻 Програмне забезпечення',
      camera: '📷 Заміна камери',
      charging: '⚡ Роз\'єм зарядки',
      other: '🔩 Інше'
    };

    const categoryColors = {
      screen: 'icon-blue', battery: 'icon-green', motherboard: 'icon-orange',
      'water-damage': 'icon-cyan', software: 'icon-purple', camera: 'icon-pink',
      charging: 'icon-yellow', other: 'icon-gray'
    };

    const categoryIcons = {
      screen: '📱', battery: '🔋', motherboard: '🔧',
      'water-damage': '💧', software: '💻', camera: '📷',
      charging: '⚡', other: '🔩'
    };

    function categoryLabel(cat) { return categoryMap[cat] || cat || 'Услуга'; }
    function getCategoryColor(cat) { return categoryColors[cat] || 'icon-gray'; }
    function getCategoryIcon(cat) { return categoryIcons[cat] || '🔧'; }

    const hasActiveFilters = computed(() =>
      filters.value.category || filters.value.minPrice || filters.value.maxPrice || filters.value.search
    );

    const displayServices = computed(() => {
      let list = services.value.length ? services.value : FALLBACK_SERVICES;
      if (filters.value.category) list = list.filter(s => s.category === filters.value.category);
      if (filters.value.minPrice) list = list.filter(s => s.price >= filters.value.minPrice);
      if (filters.value.maxPrice) list = list.filter(s => s.price <= filters.value.maxPrice);
      if (filters.value.search) {
        const q = filters.value.search.toLowerCase();
        list = list.filter(s => s.name.toLowerCase().includes(q) || (s.description || '').toLowerCase().includes(q));
      }
      return list;
    });

    function applyFilters() { /* computed handles it */ }
    function clearFilters() { filters.value = { category: '', minPrice: null, maxPrice: null, search: '' }; }

    async function loadServices() {
      loading.value = true;
      try {
        const res = await axios.get(`${API_URL}/services`);
        services.value = res.data || [];
      } catch { services.value = []; }
      finally { loading.value = false; }
    }

    function orderService(service) {
      router.push({ name: 'CreateOrder', query: { serviceId: service._id || service.id } });
    }

    onMounted(loadServices);

    return { filters, loading, displayServices, hasActiveFilters, categoryLabel, getCategoryColor, getCategoryIcon, applyFilters, clearFilters, orderService };
  }
};
</script>

<style scoped>
* { box-sizing: border-box; }
.sp-wrap { max-width: 1100px; margin: 0 auto; }

/* Header */
.sp-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; gap: 1rem; }
.sp-title { font-size: 28px; font-weight: 800; color: #26215c; }
.sp-sub { font-size: 14px; color: #888; margin-top: 4px; }
.sp-btn-order { background: #534ab7; color: #fff; padding: 11px 22px; border-radius: 10px; font-size: 14px; font-weight: 700; text-decoration: none; white-space: nowrap; transition: background 0.15s; display: inline-block; }
.sp-btn-order:hover { background: #7f77dd; }

/* Stats */
.sp-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1rem; margin-bottom: 2rem; }
.sp-stat { background: #fff; border: 1px solid #e0dff8; border-radius: 14px; padding: 1rem; text-align: center; }
.sp-stat-val { display: block; font-size: 20px; font-weight: 800; color: #534ab7; }
.sp-stat-lbl { display: block; font-size: 12px; color: #888; margin-top: 2px; }

/* Filters */
.sp-filters { display: grid; grid-template-columns: 2fr 1fr 1fr 2fr; gap: 1rem; background: #fff; border: 1px solid #e0dff8; border-radius: 16px; padding: 1.25rem; margin-bottom: 1rem; }
.sp-filter-group { display: flex; flex-direction: column; gap: 6px; }
.sp-filter-label { font-size: 12px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: 0.5px; }
.sp-select, .sp-input { background: #f4f3ff; border: 1.5px solid #e0dff8; border-radius: 8px; padding: 9px 12px; font-size: 14px; color: #26215c; outline: none; transition: border-color 0.15s; width: 100%; }
.sp-select:focus, .sp-input:focus { border-color: #534ab7; background: #fff; }

/* Active filters */
.sp-active-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 1.25rem; }
.sp-filter-tag { display: flex; align-items: center; gap: 6px; background: #eeedfe; color: #534ab7; font-size: 13px; font-weight: 600; padding: 5px 10px; border-radius: 20px; }
.sp-filter-tag button { background: none; border: none; cursor: pointer; color: #534ab7; font-size: 16px; line-height: 1; padding: 0; }
.sp-clear-all { background: none; border: none; color: #e05252; font-size: 13px; font-weight: 600; cursor: pointer; margin-left: 4px; padding: 5px 10px; }
.sp-clear-all:hover { text-decoration: underline; }

/* Loading */
.sp-loading { display: flex; align-items: center; gap: 12px; color: #888; padding: 3rem; justify-content: center; }
.sp-spinner { width: 24px; height: 24px; border: 2px solid #e0dff8; border-top-color: #534ab7; border-radius: 50%; animation: spin 0.8s linear infinite; flex-shrink: 0; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Empty */
.sp-empty { text-align: center; background: #fff; border: 1px solid #e0dff8; border-radius: 20px; padding: 4rem 2rem; }
.sp-empty-icon { font-size: 56px; margin-bottom: 1rem; }
.sp-empty h2 { font-size: 22px; font-weight: 800; color: #26215c; margin-bottom: 0.5rem; }
.sp-empty p { font-size: 14px; color: #888; }
.sp-link-btn { background: none; border: none; color: #534ab7; font-weight: 700; cursor: pointer; font-size: 14px; text-decoration: underline; }

/* Grid */
.sp-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.25rem; }

/* Card */
.sp-card { background: #fff; border: 1px solid #e0dff8; border-radius: 18px; padding: 1.5rem; display: flex; flex-direction: column; gap: 0; position: relative; transition: box-shadow 0.2s, transform 0.2s; }
.sp-card:hover { box-shadow: 0 8px 32px rgba(83,74,183,0.12); transform: translateY(-2px); }

.sp-popular-badge { position: absolute; top: 14px; right: 14px; background: #fff3cd; color: #e59f0a; font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 20px; }

.sp-card-icon-wrap { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 1rem; flex-shrink: 0; }
.sp-card-icon { font-size: 24px; }
.icon-blue { background: #eef3ff; }
.icon-green { background: #e1f5ee; }
.icon-orange { background: #fff4e6; }
.icon-cyan { background: #e0f7fa; }
.icon-purple { background: #eeedfe; }
.icon-pink { background: #fce4ec; }
.icon-yellow { background: #fef9e7; }
.icon-gray { background: #f4f3ff; }

.sp-card-body { flex: 1; margin-bottom: 1rem; }
.sp-card-category { font-size: 11px; font-weight: 700; color: #534ab7; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
.sp-card-name { font-size: 16px; font-weight: 800; color: #26215c; margin-bottom: 8px; line-height: 1.3; }
.sp-card-desc { font-size: 13px; color: #888; line-height: 1.5; }

.sp-compat { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 1rem; }
.sp-compat-tag { background: #f4f3ff; color: #534ab7; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 12px; }
.sp-compat-more { background: #e0dff8; color: #534ab7; font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 12px; }

.sp-card-meta { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-top: 1px solid #f0effc; border-bottom: 1px solid #f0effc; margin-bottom: 1rem; }
.sp-meta-time { display: flex; align-items: center; gap: 5px; font-size: 13px; color: #888; }
.sp-meta-icon { font-size: 14px; }
.sp-meta-price { font-size: 22px; font-weight: 800; color: #1d9e75; }

.sp-card-btn { background: linear-gradient(135deg, #534ab7, #7f77dd); color: #fff; border: none; padding: 11px; border-radius: 10px; font-size: 14px; font-weight: 700; cursor: pointer; width: 100%; transition: opacity 0.15s, transform 0.15s; }
.sp-card-btn:hover { opacity: 0.9; transform: translateY(-1px); }

/* CTA */
.sp-cta { background: linear-gradient(135deg, #26215c, #1a3a2a); border-radius: 20px; padding: 2rem; display: flex; justify-content: space-between; align-items: center; gap: 1.5rem; margin-top: 2.5rem; }
.sp-cta-content { display: flex; align-items: center; gap: 1rem; color: #fff; }
.sp-cta-icon { font-size: 36px; flex-shrink: 0; }
.sp-cta-content h3 { font-size: 18px; font-weight: 800; margin-bottom: 4px; }
.sp-cta-content p { font-size: 13px; color: rgba(255,255,255,0.65); line-height: 1.5; }
.sp-cta-btn { background: #1d9e75; color: #fff; padding: 12px 24px; border-radius: 10px; font-size: 14px; font-weight: 700; text-decoration: none; white-space: nowrap; transition: background 0.15s; flex-shrink: 0; }
.sp-cta-btn:hover { background: #25bf8e; }

@media (max-width: 900px) {
  .sp-grid { grid-template-columns: repeat(2, 1fr); }
  .sp-stats { grid-template-columns: repeat(2, 1fr); }
  .sp-filters { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 600px) {
  .sp-header { flex-direction: column; }
  .sp-grid { grid-template-columns: 1fr; }
  .sp-stats { grid-template-columns: repeat(2, 1fr); }
  .sp-filters { grid-template-columns: 1fr; }
  .sp-cta { flex-direction: column; text-align: center; }
  .sp-cta-content { flex-direction: column; text-align: center; }
}
</style>
